import { LazyImage } from '../../components/LazyImage';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';

export type PopoverContent = {
  heading: string;
  previewPageId: string;
  previewPageIds?: string[]; // when set, render these (up to 3) side-by-side instead of previewPageId
  morePages?: number; // pages beyond the previewed ones; rendered as a "+N" tile
  classLabel?: string | null;
  classScore?: number | null; // 0..1
  cdTitle?: string | null;
  cdDescription?: string | null;
  x: number;
  y: number;
};

// Floating preview shown while hovering a lineage node. Position is the cursor
// point (viewport-fixed); flips left / clamps so it never spills off-screen.
export function LineagePopover({ content }: { content: PopoverContent }) {
  const previewIds = (content.previewPageIds?.length ? content.previewPageIds : [content.previewPageId]).slice(0, 3);
  const morePages = content.morePages ?? 0;
  const base = previewIds.length >= 3 ? 384 : previewIds.length === 2 ? 320 : 248;
  const W = base + (morePages > 0 ? 44 : 0);
  const flipLeft = content.x + W + 24 > window.innerWidth;
  const left = flipLeft ? Math.max(8, content.x - W - 16) : content.x + 16;
  const top = Math.min(content.y + 16, window.innerHeight - 320);
  const pct = content.classScore != null ? `${Math.round(content.classScore * 100)}%` : null;

  return (
    <div
      className="pointer-events-none fixed z-50 rounded-lg border border-gray-200 bg-white p-2 shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
      style={{ left, top: Math.max(8, top), width: W }}
    >
      <div className="mb-2 flex gap-1">
        {previewIds.map((id) => (
          <div
            key={id}
            className="min-w-0 flex-1 overflow-hidden rounded border border-gray-100 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900"
            style={{ maxHeight: 200 }}
          >
            <LazyImage src={pagePreviewSrc(id, { resize: 'x400' })} className="w-full object-contain" />
          </div>
        ))}
        {morePages > 0 && (
          <div className="flex w-9 shrink-0 items-center justify-center rounded border border-gray-100 bg-gray-50 text-[11px] font-semibold text-gray-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
            +{morePages}
          </div>
        )}
      </div>

      <div className="text-xs font-medium text-gray-900 dark:text-zinc-100">{content.heading}</div>

      {content.classLabel && (
        <div className="mt-1 flex items-center gap-1.5">
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[11px] font-semibold text-blue-800 dark:bg-blue-900/50 dark:text-blue-200">
            {content.classLabel}
          </span>
          {pct && <span className="text-[11px] text-gray-500 dark:text-zinc-400">{pct}</span>}
        </div>
      )}

      {content.cdTitle && <div className="mt-1.5 text-[11px] font-semibold text-gray-800 dark:text-zinc-200">{content.cdTitle}</div>}
      {content.cdDescription && (
        <p className="mt-0.5 line-clamp-4 text-[11px] leading-snug text-gray-500 dark:text-zinc-400">{content.cdDescription}</p>
      )}
    </div>
  );
}
